import * as Schema from "effect/Schema";

import {
  CommandId,
  EnvironmentId,
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const ManagedThreadWorktreeId = TrimmedNonEmptyString.pipe(
  Schema.brand("ManagedThreadWorktreeId"),
);
export type ManagedThreadWorktreeId = typeof ManagedThreadWorktreeId.Type;

export const WorktreeName = TrimmedNonEmptyString.check(Schema.isMaxLength(80));
export type WorktreeName = typeof WorktreeName.Type;

export const ThreadWorktreeLocation = Schema.Union([
  Schema.Struct({ type: Schema.Literal("host-store") }),
  Schema.Struct({ type: Schema.Literal("project") }),
  Schema.Struct({
    type: Schema.Literal("custom"),
    parentPath: TrimmedNonEmptyString,
  }),
]);
export type ThreadWorktreeLocation = typeof ThreadWorktreeLocation.Type;

export const ThreadWorkspaceSelection = Schema.Union([
  Schema.Struct({ type: Schema.Literal("current-checkout") }),
  Schema.Struct({
    type: Schema.Literal("available-worktree"),
    path: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    type: Schema.Literal("new-worktree"),
    location: ThreadWorktreeLocation,
  }),
]);
export type ThreadWorkspaceSelection = typeof ThreadWorkspaceSelection.Type;

export const AvailableProjectWorktree = Schema.Struct({
  path: TrimmedNonEmptyString,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  managedWorktreeId: Schema.NullOr(ManagedThreadWorktreeId),
  name: Schema.optionalKey(Schema.NullOr(WorktreeName)),
});
export type AvailableProjectWorktree = typeof AvailableProjectWorktree.Type;

export const ManagedThreadWorktreeState = Schema.Literals(["present", "missing"]);
export type ManagedThreadWorktreeState = typeof ManagedThreadWorktreeState.Type;

export const ManagedThreadWorktree = Schema.Struct({
  id: ManagedThreadWorktreeId,
  name: Schema.optionalKey(WorktreeName),
  environmentId: EnvironmentId,
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  projectRoot: TrimmedNonEmptyString,
  path: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  location: ThreadWorktreeLocation,
  threadIds: Schema.Array(ThreadId),
  state: ManagedThreadWorktreeState,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ManagedThreadWorktree = typeof ManagedThreadWorktree.Type;

export const AvailableProjectWorktreesInput = Schema.Struct({ projectId: ProjectId });
export type AvailableProjectWorktreesInput = typeof AvailableProjectWorktreesInput.Type;

export const AvailableProjectWorktreesResult = Schema.Struct({
  isGitRepository: Schema.Boolean,
  unavailableReason: Schema.NullOr(TrimmedNonEmptyString),
  worktrees: Schema.Array(AvailableProjectWorktree),
});
export type AvailableProjectWorktreesResult = typeof AvailableProjectWorktreesResult.Type;

export const ManagedThreadWorktreesListInput = Schema.Struct({});
export type ManagedThreadWorktreesListInput = typeof ManagedThreadWorktreesListInput.Type;

export const ManagedThreadWorktreesListResult = Schema.Struct({
  worktrees: Schema.Array(ManagedThreadWorktree),
});
export type ManagedThreadWorktreesListResult = typeof ManagedThreadWorktreesListResult.Type;

export const ManagedThreadWorktreeCreateInput = Schema.Struct({
  projectId: ProjectId,
  ownerThreadId: ThreadId,
  name: WorktreeName,
  baseBranch: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  location: ThreadWorktreeLocation,
  startFromOrigin: Schema.Boolean,
});
export type ManagedThreadWorktreeCreateInput = typeof ManagedThreadWorktreeCreateInput.Type;

export const ManagedThreadWorktreeCreateResult = Schema.Struct({
  id: ManagedThreadWorktreeId,
  name: WorktreeName,
  path: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeCreateResult = typeof ManagedThreadWorktreeCreateResult.Type;

export const ManagedThreadWorktreeDeleteInput = Schema.Struct({
  commandId: CommandId,
  worktreeId: ManagedThreadWorktreeId,
  projectId: ProjectId,
  expectedRevision: IsoDateTime,
  intent: Schema.Literal("delete-managed-thread-worktree"),
});
export type ManagedThreadWorktreeDeleteInput = typeof ManagedThreadWorktreeDeleteInput.Type;

export const ManagedThreadWorktreeDeleteProjectInput = Schema.Struct({ projectId: ProjectId });
export type ManagedThreadWorktreeDeleteProjectInput =
  typeof ManagedThreadWorktreeDeleteProjectInput.Type;

export const ManagedThreadWorktreeDeleteAllInput = Schema.Struct({});
export type ManagedThreadWorktreeDeleteAllInput = typeof ManagedThreadWorktreeDeleteAllInput.Type;

export const ManagedThreadWorktreeDeleteOutcome = Schema.Struct({
  worktreeId: ManagedThreadWorktreeId,
  outcome: Schema.Literals(["deleted", "busy", "failed", "not-found", "revision-conflict"]),
  detail: Schema.optional(TrimmedNonEmptyString),
  authoritativeRevision: Schema.optional(IsoDateTime),
});
export type ManagedThreadWorktreeDeleteOutcome = typeof ManagedThreadWorktreeDeleteOutcome.Type;

export const ManagedThreadWorktreeDeleteResult = Schema.Struct({
  outcomes: Schema.Array(ManagedThreadWorktreeDeleteOutcome),
});
export type ManagedThreadWorktreeDeleteResult = typeof ManagedThreadWorktreeDeleteResult.Type;

const ExperimentEvidenceDigest = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));

export const ManagedThreadWorktreeEvidenceRecoveryCandidate = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  snapshotId: TrimmedNonEmptyString,
  digest: ExperimentEvidenceDigest,
  byteLength: NonNegativeInt,
});
export type ManagedThreadWorktreeEvidenceRecoveryCandidate =
  typeof ManagedThreadWorktreeEvidenceRecoveryCandidate.Type;

export const ManagedThreadWorktreeEvidenceRecoveryFinding = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  digest: ExperimentEvidenceDigest,
  code: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeEvidenceRecoveryFinding =
  typeof ManagedThreadWorktreeEvidenceRecoveryFinding.Type;

export const ManagedThreadWorktreeEvidenceRecovery = Schema.Struct({
  projectId: ProjectId,
  projectTitle: TrimmedNonEmptyString,
  projectRoot: TrimmedNonEmptyString,
  worktreeId: ManagedThreadWorktreeId,
  sourceCheckoutRoot: TrimmedNonEmptyString,
  candidateCount: NonNegativeInt,
  totalBytes: NonNegativeInt,
  reclaimableCandidateCount: NonNegativeInt,
  reclaimable: Schema.Boolean,
  readableSnapshotIds: Schema.Array(TrimmedNonEmptyString),
  findings: Schema.Array(ManagedThreadWorktreeEvidenceRecoveryFinding),
  candidates: Schema.Array(ManagedThreadWorktreeEvidenceRecoveryCandidate),
  confirmation: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeEvidenceRecovery =
  typeof ManagedThreadWorktreeEvidenceRecovery.Type;

export const ManagedThreadWorktreeListEvidenceRecoveriesInput = Schema.Struct({});
export type ManagedThreadWorktreeListEvidenceRecoveriesInput =
  typeof ManagedThreadWorktreeListEvidenceRecoveriesInput.Type;

export const ManagedThreadWorktreeListEvidenceRecoveriesResult = Schema.Struct({
  recoveries: Schema.Array(ManagedThreadWorktreeEvidenceRecovery),
});
export type ManagedThreadWorktreeListEvidenceRecoveriesResult =
  typeof ManagedThreadWorktreeListEvidenceRecoveriesResult.Type;

export const ManagedThreadWorktreeRestoreEvidenceInput = Schema.Struct({
  projectId: ProjectId,
  worktreeId: ManagedThreadWorktreeId,
  digest: ExperimentEvidenceDigest,
  relativePath: TrimmedNonEmptyString,
  destinationCheckoutRoot: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeRestoreEvidenceInput =
  typeof ManagedThreadWorktreeRestoreEvidenceInput.Type;

export const ManagedThreadWorktreeRestoreEvidenceResult = Schema.Struct({
  outcome: Schema.Literals(["restored", "identical"]),
  digest: ExperimentEvidenceDigest,
  path: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeRestoreEvidenceResult =
  typeof ManagedThreadWorktreeRestoreEvidenceResult.Type;

export const ManagedThreadWorktreeDiscardEvidenceInput = Schema.Struct({
  projectId: ProjectId,
  worktreeId: ManagedThreadWorktreeId,
  confirmation: TrimmedNonEmptyString,
});
export type ManagedThreadWorktreeDiscardEvidenceInput =
  typeof ManagedThreadWorktreeDiscardEvidenceInput.Type;

export const ManagedThreadWorktreeDiscardEvidenceResult = Schema.Struct({
  discardedCandidateCount: NonNegativeInt,
  discardedBytes: NonNegativeInt,
});
export type ManagedThreadWorktreeDiscardEvidenceResult =
  typeof ManagedThreadWorktreeDiscardEvidenceResult.Type;

export class ManagedThreadWorktreeEvidenceRecoveryError extends Schema.TaggedErrorClass<ManagedThreadWorktreeEvidenceRecoveryError>()(
  "ManagedThreadWorktreeEvidenceRecoveryError",
  {
    reason: Schema.Literals([
      "not-found",
      "conflict",
      "unsafe-destination",
      "confirmation-mismatch",
      "io-failed",
    ]),
    message: TrimmedNonEmptyString,
  },
) {}

export class ManagedThreadWorktreeError extends Schema.TaggedErrorClass<ManagedThreadWorktreeError>()(
  "ManagedThreadWorktreeError",
  {
    reason: Schema.Literals([
      "project-not-found",
      "not-git-repository",
      "invalid-location",
      "unsafe-ignore-rule",
      "persistence-failed",
      "git-failed",
    ]),
    message: TrimmedNonEmptyString,
  },
) {}
